import { useState, useRef } from "react";
import { Check, Star, XCircle, Wand2, Heart } from "lucide-react";
import { mediaUrl } from "@/lib/galleryApi";
import { RetryImg } from "@/components/gallery/RetryImg";

const PAGE = 240;

function Tile({ p, selected, isCover, onToggle, onOpen }) {
  return (
    <div
      className={`relative aspect-square rounded-lg overflow-hidden group cursor-pointer bg-[#0B1120] ring-2 transition-shadow ${selected ? "ring-[#0099CC]" : "ring-transparent hover:ring-[#1A2332]"} ${p.is_rejected ? "opacity-40" : ""}`}
      style={{ contentVisibility: "auto", containIntrinsicSize: "160px" }}
      data-testid={`photo-${p.id}`}
    >
      <RetryImg src={mediaUrl(p.thumb_url)} alt={p.filename} loading="lazy" decoding="async" className="w-full h-full object-cover" onClick={onOpen} />
      <button onClick={onToggle} className="absolute top-1.5 left-1.5" data-testid={`photo-select-${p.id}`} aria-label="Selectionner">
        <span className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-opacity ${selected ? "bg-[#0099CC] border-[#0099CC]" : "border-white/60 bg-black/40 opacity-0 group-hover:opacity-100"}`}>
          {selected && <Check className="w-3 h-3 text-white" />}
        </span>
      </button>
      <div className="absolute top-1.5 right-1.5 flex gap-1">
        {p.is_favorite && <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B] drop-shadow" />}
        {p.client_favorites?.length > 0 && <Heart className="w-4 h-4 text-[#F43F5E] fill-[#F43F5E] drop-shadow" data-testid={`client-fav-badge-${p.id}`} />}
        {p.is_rejected && <XCircle className="w-4 h-4 text-red-400" />}
      </div>
      <div className="absolute bottom-1.5 left-1.5 flex gap-1">
        {p.status === "published" && <span className="bg-[#10B981] text-white text-[8px] px-1.5 py-0.5 rounded font-bold tracking-wide">PUBLIEE</span>}
        {p.has_processed && <span className="bg-[#F97316] text-white text-[8px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5"><Wand2 className="w-2 h-2" />HD</span>}
        {isCover && <span className="bg-white text-black text-[8px] px-1.5 py-0.5 rounded font-bold">COUVERTURE</span>}
      </div>
    </div>
  );
}

export function PhotoGrid({ photos, selected, setSelected, coverId, onOpen }) {
  const [limit, setLimit] = useState(PAGE);
  const lastIdx = useRef(null);

  const toggle = (idx, e) => {
    const id = photos[idx].id;
    const anchor = lastIdx.current;
    const shift = e.shiftKey;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && anchor !== null) {
        const [a, b] = [anchor, idx].sort((x, y) => x - y);
        photos.slice(a, b + 1).forEach((p) => next.add(p.id));
      } else if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastIdx.current = idx;
  };

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2" data-testid="photo-grid">
        {photos.slice(0, limit).map((p, i) => (
          <Tile key={p.id} p={p} selected={selected.has(p.id)} isCover={coverId === p.id} onToggle={(e) => toggle(i, e)} onOpen={() => onOpen(i)} />
        ))}
      </div>
      {photos.length > limit && (
        <div className="text-center mt-6">
          <button onClick={() => setLimit((l) => l + PAGE)} className="text-sm text-[#0099CC] hover:underline" data-testid="grid-show-more">
            Afficher plus ({photos.length - limit} restantes)
          </button>
        </div>
      )}
    </>
  );
}
